import { type ComponentType, useMemo } from 'react';
import { type BaseParams, NodeData } from './base';
import type { ContextItemData, ComponentItemData } from './types';
import { providePageContext, usePageContext } from './page';
import { createAssistantContextItem } from './chat';

export type ComponentImplementation = ComponentType<any>;
export type NamedComponents = Record<string, ComponentImplementation>;

/**
 * Type used to create a component context item.
 */
export interface CreateComponentContextParams extends BaseParams {
  components: Record<string, ComponentType<any>>;
  prompt: string;
  namespace?: string;
}

export class ComponentNodeData extends NodeData {
  public components: Record<string, ComponentType<any>>;
  public prompt: string;
  public namespace: string;

  constructor(params: CreateComponentContextParams) {
    const namespace = params.namespace || 'components';
    super({ ...params, id: `${namespace}-${Object.keys(params.components).join('-')}`, type: 'component' });
    this.components = params.components;
    this.prompt = params.prompt;
    this.namespace = namespace;
  }

  formatForLLM(codeElementIds?: string[]): ContextItemData {
    return {
      type: this.type,
      codeElementIds,
      data: {
        name: `Custom UI components (${this.namespace})`,
        prompt: this.prompt
          ? this.prompt +
            '\n\nIMPORTANT: The instructions and components described above are scoped to this specific context item only. Do not apply them to unrelated queries or components.'
          : '',
      } satisfies Omit<ComponentItemData, 'components' | 'text' | 'namespace'>,
    };
  }
}

/**
 * React hook that provides all components available globally.
 * Components are available everywhere, regardless of URL patterns.
 *
 * @returns Object containing all components available globally, keyed by namespace.componentName
 */
export function usePageComponents(): Record<string, ComponentImplementation> {
  const pageContext = usePageContext({ allowComponents: true, allowQuestions: false });

  // Use useMemo to avoid recreating the components object on every render
  const components = useMemo(() => {
    const allComponents: Record<string, ComponentImplementation> = {};

    // Filter for component context items and extract their components
    for (const item of pageContext.filter((item) => item.node.data?.components)) {
      const componentData = item.node.data;
      if (componentData?.components && Object.keys(componentData.components).length > 0) {
        const namespace = componentData.namespace || componentData.name || 'components';

        // Add each component with namespace_componentName format
        for (const [componentName, component] of Object.entries(componentData.components)) {
          allComponents[`${namespace}_${componentName}`] = component as ComponentImplementation;
        }
      }
    }

    return allComponents;
  }, [pageContext]);

  return components;
}

/**
 * Provide components to the assistant.
 *
 * Components are always available globally, but the prompt is only applied when the URL pattern matches.
 *
 * For catch-all URL patterns, returns a setter that takes a context array.
 * For specific URL patterns, returns a setter that takes components and prompt separately.
 *
 * @param prompt - The prompt to use for the components (only applied when URL matches).
 * @param namespace - The namespace to use for the components.
 * @param components - The components to provide (always available).
 * @param urlPattern - The URL pattern to match for the prompt (components are always available).
 * @returns A setter function to update the context, with an unregister method attached
 */
export function provideComponents(
  prompt: string,
  namespace: string,
  components: Record<string, ComponentImplementation>,
  urlPattern: string | RegExp = /.*/
): ((newComponents: Record<string, ComponentImplementation>, newPrompt: string) => void) & { unregister: () => void } {
  // Check if the URL pattern is catch-all (.*) - if so, register everything together
  const isCatchAll = (pattern: string | RegExp) => {
    if (pattern instanceof RegExp) {
      return pattern.source === '.*' && pattern.flags === '';
    }
    return pattern === '.*' || pattern === '/.*/';
  };

  if (isCatchAll(urlPattern)) {
    // If pattern is catch-all, register components and prompt together globally
    const setContext = providePageContext(/.*/, [
      createAssistantContextItem('component', {
        components,
        prompt,
        namespace,
      }),
    ]);

    // Create a wrapper setter that matches the expected signature
    const setter = (newComponents: Record<string, ComponentImplementation>, newPrompt: string) => {
      setContext([
        createAssistantContextItem('component', {
          components: newComponents,
          prompt: newPrompt,
          namespace,
        }),
      ]);
    };

    // Attach unregister method
    setter.unregister = setContext.unregister;

    return setter;
  }

  // For specific URL patterns, separate components (global) from prompt (URL-specific)
  const globalComponents = providePageContext(/.*/, [
    createAssistantContextItem('component', {
      components,
      prompt: '', // No prompt for global components
      namespace,
    }),
  ]);

  // Only register prompt for specific URL pattern
  const promptContext = providePageContext(urlPattern, [
    createAssistantContextItem('component', {
      components: {}, // No components, just prompt
      prompt,
      namespace,
    }),
  ]);

  // Return a setter that properly separates components and prompt
  // When updating, we extract components and prompt from the new data and update each registration appropriately
  const setter = (newComponents: Record<string, ComponentImplementation>, newPrompt: string) => {
    // Update global components registration with new components (but empty prompt)
    globalComponents([
      createAssistantContextItem('component', {
        components: newComponents,
        prompt: '', // No prompt for global components
        namespace,
      }),
    ]);

    // Update URL-specific registration with new prompt (but empty components)
    promptContext([
      createAssistantContextItem('component', {
        components: {}, // No components, just prompt
        prompt: newPrompt,
        namespace,
      }),
    ]);
  };

  // Attach unregister method that cleans up both registrations
  setter.unregister = () => {
    globalComponents.unregister();
    promptContext.unregister();
  };

  return setter;
}
